import { createContext, useContext, useEffect, useState, type FC } from "react";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import type { SlotContext, SlotProviderProps } from "@b2bportal/core-utilities";

// Render sites may pass a className for placement (e.g. grid positioning).
const EmptySlot: FC<{ className?: string }> = () => null;

// Lazy-load @tenant/hotels to avoid circular dependency (packages/app → create → @tenant/hotels → packages → defaultHotelSlots before init).
// Async so we use dynamic import() in ESM; callers use useDefaultHotelSlots() or await defaultHotelSlots().
export const defaultHotelSlots = async () => {
  const {
    HotelCardPrice,
    HotelAvailabilitySearch,
    HotelBreadcrumbs,
    HotelShopPrice,
    HotelRoomCardRates,
    LinkToHotel,
    HotelListError,
    HotelShopBookingSection,
    HotelMapRedoSearchButton,
    HotelMapMarkers,
    HotelRoomSelection,
    FilterDialog,
    StayTypeFilter,
  } = await import("@tenant/hotels");
  return {
    [CoreHotelsComponent.HotelCardPrice]: HotelCardPrice,
    [CoreHotelsComponent.HotelAvailabilitySearch]: HotelAvailabilitySearch,
    [CoreHotelsComponent.HotelBreadcrumbs]: HotelBreadcrumbs,
    [CoreHotelsComponent.HotelShopPrice]: HotelShopPrice,
    [CoreHotelsComponent.HotelRoomCardRates]: HotelRoomCardRates,
    [CoreHotelsComponent.HotelLink]: LinkToHotel,
    [CoreHotelsComponent.HotelListError]: HotelListError,
    [CoreHotelsComponent.HotelShopBookingSection]: HotelShopBookingSection,
    [CoreHotelsComponent.HotelMapRedoSearchButton]: HotelMapRedoSearchButton,
    [CoreHotelsComponent.HotelMapMarkers]: HotelMapMarkers,
    [CoreHotelsComponent.HotelRoomSelection]: HotelRoomSelection,
    [CoreHotelsComponent.FilterDialog]: FilterDialog,
    [CoreHotelsComponent.StayTypeFilter]: StayTypeFilter,
    [CoreHotelsComponent.HotelShopHeader]: EmptySlot,
    [CoreHotelsComponent.HotelAvailabilityBanner]: EmptySlot,
    [CoreHotelsComponent.HotelRoomSelectionBanner]: EmptySlot,
  };
};

export type HotelSlotType = Awaited<ReturnType<typeof defaultHotelSlots>>;

let defaultSlotsPromise: Promise<HotelSlotType> | null = null;

const getDefaultHotelSlotsPromise = (): Promise<HotelSlotType> => {
  if (defaultSlotsPromise == null) {
    defaultSlotsPromise = defaultHotelSlots();
  }
  return defaultSlotsPromise;
};

/** Resolves default hotel slots from @tenant/hotels. Use in providers that need to merge with overrides. */
export const useDefaultHotelSlots = (): HotelSlotType | null => {
  const [slots, setSlots] = useState<HotelSlotType | null>(null);
  useEffect(() => {
    let cancelled = false;
    getDefaultHotelSlotsPromise().then((result) => {
      if (!cancelled) {
        setSlots(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return slots;
};

const HotelSlotContext = createContext<SlotContext<HotelSlotType> | undefined>(
  undefined
);

export const HotelSlotProvider = ({
  context,
  children,
}: SlotProviderProps<HotelSlotType>) => {
  return (
    <HotelSlotContext.Provider value={context}>
      {children}
    </HotelSlotContext.Provider>
  );
};

const useSlotSystem = () => {
  const context = useContext(HotelSlotContext);
  if (context == null) {
    throw new Error("useSlots must be used within a HotelSlotProvider");
  }
  return context;
};

export const useHotelSlot = () => {
  const context = useSlotSystem();
  return context.components;
};
